/**
 * Decorative double-border frame shell.
 *
 * `className` is for frame-level layout only (sizing, overflow, shadow, cursor).
 * Do NOT pass padding classes (`p-*`, `px-*`, `py-*`, `pt-*`, etc.) — they warp
 * the border render. Apply padding on a child wrapper inside the frame instead.
 */
export interface StandardFrameProps {
  children?: React.ReactNode;
  className?: string;
  rounded?: boolean;
  variant:
    | "blue"
    | "gray"
    | "black"
    | "purple"
    | "orange"
    | "green"
    | "beige"
    | "red"
    | "yellow"
    | "gold"
    | "garnet"
    | "white"
    | "night";
  onClick?: (...args: any[]) => any;
  style?: React.CSSProperties;
}

export const StandardFrame = ({
  children,
  className,
  variant,
  rounded = false,
  onClick,
  style,
}: StandardFrameProps) => {
  const variantClasses: Record<StandardFrameProps["variant"], any> = {
    blue: {
      bg: "bg-pk-blue-1",
      border: "border-pk-blue-5",
    },
    gray: {
      bg: "bg-pk-gray-3",
      border: "border-pk-gray-4",
    },
    black: {
      bg: "bg-pk-gray-1",
      border: "border-pk-gray-2",
    },
    purple: {
      bg: "bg-pk-purple-3",
      border: "border-pk-purple-5",
    },
    orange: {
      bg: "bg-pk-orange-3",
      border: "border-pk-orange-4",
    },
    green: {
      bg: "bg-pk-green-3",
      border: "border-pk-green-4",
    },
    beige: {
      bg: "bg-pk-beige-2",
      border: "border-pk-yellow-1",
    },
    red: {
      bg: "bg-pk-red-3",
      border: "border-pk-red-4",
    },
    yellow: {
      bg: "bg-pk-yellow-1",
      border: "border-pk-yellow-3",
    },
    gold: {
      bg: "bg-pk-gold-2",
      border: "border-pk-gold-4",
    },
    garnet: {
      bg: "bg-pk-garnet-2",
      border: "border-pk-garnet-4",
    },
    white: {
      bg: "bg-pk-gray-4",
      border: "border-pk-gray-5",
    },
    night: {
      bg: "bg-pk-night-1",
      border: "border-pk-night-2",
    },
  } as const;

  return (
    <div
      className={`border-4 relative border-gray-900 z-10 ${
        rounded && "rounded-lg"
      } ${className ?? ""}`}
      onClick={onClick}
    >
      <div
        className={`${rounded && "rounded-md"} w-full h-full ${
          variantClasses[variant].bg
        } border-4 ${variantClasses[variant].border}`}
        style={style}
      >
        {/* Texture Behind the Components*/}
        <div
          className="absolute inset-0 z-0 pointer-events-none rounded-2xl"
          style={{
            backgroundImage: "url(/assets/app-ui/glass-2.png)",
            backgroundSize: "cover",
            backgroundPosition: "left",
            backgroundRepeat: "repeat",
            mixBlendMode: "multiply",
            opacity: 0.35,
          }}
        />

        <div
          className={`${
            rounded && "rounded-sm"
          } w-full h-full flex flex-col relative z-1`}
        >
          {children}
        </div>
      </div>
    </div>
  );
};
